import { type ChangeEvent, type MouseEvent, memo, useCallback, useMemo, useState } from 'react';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { IconButton } from 'components/mui/common/IconButton';
import { Delete, DragIndicator, PlaylistAdd } from '@mui/icons-material';
import { Controller, get, useFormContext, useWatch } from 'react-hook-form';
import { AccordionDetails, FormControlLabel, Typography } from '@mui/material';
import { Accordion, AccordionSummary, ExpandIcon, Title } from './DashboardRow.styles';
import { Button } from 'components/mui/common/Button';
import { useTranslation } from 'react-i18next';
import { Checkbox } from 'components/mui/common/Checkbox';
import type { AgendaViewType } from '../item/agendaViewItem/AddAgendaViewModal';
import { AddAgendaViewModal } from '../item/agendaViewItem/AddAgendaViewModal';
import { useBooleanState } from 'utils/hooks';
import { DashboardRowContent } from './DashboardRowContent';
import { getUUID } from 'utils/common';
import { useFeatureFlag } from 'utils/hooks/posthog/useFeatureFlag';

interface DashboardProps {
  id: string;
  type: 'kpiItems' | 'agendaViewItems' | 'modules';
  index: number;
  onRemove?: (index: number) => void;
  isModule?: boolean;
  isFlow?: boolean;
  isHelios?: boolean;
}

const DashboardRowBase = ({ id, index, type, onRemove, isModule, isHelios }: DashboardProps) => {
  const { t } = useTranslation();
  const enabledUserDashboard = useFeatureFlag('F20-2625');
  const [expanded, setExpanded] = useState(false);
  const [addAgendaViewOpen, toggleAgendaView] = useBooleanState(false);
  const {
    control,
    setValue,
    clearErrors,
    formState: { errors },
  } = useFormContext();
  const dashboardItem = useWatch({ name: `dashboard.${index}`, control });
  const items = useMemo(() => dashboardItem?.items || [], [dashboardItem?.items]);

  const handleAddKpi = useCallback(() => {
    setValue(
      `dashboard.${index}.items`,
      [
        ...items,
        {
          id: getUUID(),
          type: 'KPI',
          order: items.length,
          borderColor: '',
          name: '',
          isNew: true,
          kpi: {
            id: getUUID(),
          },
        },
      ],
      { shouldDirty: true, shouldValidate: true }
    );
    clearErrors(`dashboard.${index}.items`);
  }, [clearErrors, index, items, setValue]);

  const handleAddAgendaView = useCallback(
    (itemType: AgendaViewType) => {
      setValue(
        `dashboard.${index}.items`,
        [
          ...items,
          {
            id: getUUID(),
            type: itemType,
            order: items.length,
            borderColor: '',
            name: '',
            isNew: true,
            AgendaView: {
              id: getUUID(),
              moduleId: '',
              agendaId: '',
              viewId: '',
            },
          },
        ],
        { shouldDirty: true, shouldValidate: true }
      );
      clearErrors(`dashboard.${index}.items`);
    },
    [clearErrors, index, items, setValue]
  );

  const handleChangeModulesMode = useCallback(
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => setValue(`dashboard.${index}.viewMode`, checked ? 'module' : 'agenda'),
    [index, setValue]
  );

  const handleAddItem = useCallback(
    (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (type === 'kpiItems') {
        handleAddKpi();
      } else {
        if (isModule || !enabledUserDashboard) {
          handleAddAgendaView('AgendaView');
        } else {
          toggleAgendaView();
        }
      }
    },
    [enabledUserDashboard, handleAddAgendaView, handleAddKpi, isModule, toggleAgendaView, type]
  );

  const handleRemoveItem = useCallback(
    (itemIndex: number) => {
      const currentData = [...items];
      currentData.splice(itemIndex, 1);
      setValue(`dashboard.${index}.items`, currentData);
    },
    [index, items, setValue]
  );

  const handleUpdateItem = useCallback(
    (itemIndex: number, data: object /*TODO*/) => {
      setValue(`dashboard.${index}.items.${itemIndex}`, data);
    },
    [index, setValue]
  );

  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
    data: { type: 'dashboard_row' },
  });

  const style = {
    transform: CSS.Translate.toString(transform),
    transition,
    zIndex: isDragging ? 50 : 'auto',
    opacity: isDragging ? 0.5 : 1,
    position: 'relative' as const,
    display: 'block',
  };

  const error = get(errors, `dashboard.${index}.items`);

  return (
    <>
      <Accordion
        id={index.toString()}
        ref={setNodeRef}
        style={style}
        disableGutters
        square
        expanded={expanded}
        error={Boolean(error)}
        onChange={(_, val) => setExpanded(val)}>
        <AccordionSummary expanded={expanded}>
          <IconButton {...attributes} {...listeners} sx={{ cursor: 'grab', touchAction: 'none' }}>
            <DragIndicator />
          </IconButton>
          <ExpandIcon expanded={expanded} />
          <Title>
            {type === 'kpiItems' ? 'KPI' : type === 'agendaViewItems' ? t('core.settings.dashboard.views') : t('core.settings.dashboard.modules')}
          </Title>
          {error && (
            <Typography color="error" mr={2}>
              {error?.message}
            </Typography>
          )}
          {expanded &&
            (type === 'modules' ? (
              <>
                <Controller
                  name="showCounts"
                  control={control}
                  render={({ field }) => (
                    <FormControlLabel
                      onClick={(e) => e.stopPropagation()}
                      control={<Checkbox {...field} checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />}
                      label={t('core.settings.dashboard.showRecordCount')}
                    />
                  )}
                />
                <FormControlLabel
                  onClick={(e) => e.stopPropagation()}
                  control={<Checkbox checked={dashboardItem?.viewMode === 'module'} onChange={handleChangeModulesMode} />}
                  label={t('core.settings.dashboard.groupByModule')}
                />
              </>
            ) : (
              <Button startIcon={<PlaylistAdd />} variant="outlined" onClick={handleAddItem}>
                {type === 'kpiItems' ? t('core.settings.dashboard.addKPI') : t('core.settings.dashboard.addWidget')}
              </Button>
            ))}
          {Boolean(onRemove) && (
            <IconButton
              onClick={(e) => {
                e.stopPropagation();
                onRemove?.(index);
              }}>
              <Delete />
            </IconButton>
          )}
        </AccordionSummary>
        <AccordionDetails>
          <DashboardRowContent type={type} isHelios={isHelios} index={index} onRemove={handleRemoveItem} onChange={handleUpdateItem} />
        </AccordionDetails>
      </Accordion>
      <AddAgendaViewModal open={addAgendaViewOpen} onClose={toggleAgendaView} onAdd={handleAddAgendaView} />
    </>
  );
};

export const DashboardRow = memo(DashboardRowBase) as typeof DashboardRowBase;
